
import { PropType } from 'vue';
import { defineComponent } from 'vue';
import Badge from '@/components/ui/Badge.vue';
import ProposalStatus from '@/components/proposal/ProposalStatus.vue';
import { Customer } from '@/model/customer';
import OutlineButton from '@/components/ui/OutlineButton.vue';
import { Proposal } from '@/model/proposal';
import Modal from '@/components/ui/Modal.vue';
import CustomerHeader from '@/components/customer/CustomerHeader.vue';
import CustomerDetailsModal from '@/components/customer/CustomerDetailsModal.vue';
import { ProposalMutation } from '@/store/proposal/proposal.mutation';
import { ProposalAction } from '@/store/proposal/proposal.action';
import { AppMutation } from '@/store/app/app.mutation';
import { NotificationType } from '@/store/app/notification/notification-type';
import { mapGetters } from 'vuex';
import ProposalSettingsModal from '@/components/proposal/form/ProposalSettingsModal.vue';
import ProposalChangelogModal from '@/components/proposal/ProposalChangelogModal.vue';

export default defineComponent({
  name: "ProposalHeader",
  components: {
    ProposalSettingsModal, ProposalChangelogModal, CustomerDetailsModal, CustomerHeader, Modal, OutlineButton, Badge,
    ProposalStatus
  },
  emits: ['saveClick'],
  props: {
    customer: {
      required: true,
      type: Object as PropType<Customer>
    },
    proposal: {
      type: Object as PropType<Proposal>,
      required: true
    },
    showGrossMarginButton: {
      type: Boolean,
      default: false
    },
    showSaveButton: {
      type: Boolean,
      default: false
    },
    isSaving: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showClientModal: false,
      showSettingsModal: false,
      showChangelogModal: false,
      openedFromSave: false,
      settings: {
        description: '',
        validFrom: '',
        validTo: '',
        createdBy: '',
        requestedBy: ''
      }
    }
  },
  watch: {
    showSettingsModal(isOpen: boolean): void {
      if (!isOpen) {
        this.openedFromSave = false;
      }
    }
  },
  methods: {
    // fromSave: the save flow opens the modal when the description is missing and resumes on confirm.
    openSettings(fromSave = false): void {
      this.openedFromSave = fromSave;
      this.showSettingsModal = true;
    },
    handleSettingsConfirmClick(): void {
      const shouldResumeSave = this.openedFromSave;
      this.openedFromSave = false;

      if (this.isProposalEditable) {
        this.$store.commit(ProposalMutation.SET_PROPOSAL_DESCRIPTION, this.settings.description);
        this.$store.commit(ProposalMutation.SET_PROPOSAL_VALID_FROM, this.settings.validFrom);
        this.$store.commit(ProposalMutation.SET_PROPOSAL_VALID_TO, this.settings.validTo);
        this.$store.commit(ProposalMutation.SET_PROPOSAL_CREATED_BY, this.settings.createdBy);
        this.$store.commit(ProposalMutation.SET_PROPOSAL_REQUESTED_BY, this.settings.requestedBy);

        if (shouldResumeSave) {
          this.$emit('saveClick');
        }
        return;
      }

      this.$store
          .dispatch(ProposalAction.UPDATE_PROPOSAL_DESCRIPTION, {
            proposalId: String(this.proposal.id),
            description: this.settings.description
          })
          .then(() => {
            this.$store.commit(AppMutation.ADD_NOTIFICATION, {
              type: NotificationType.SUCCESS,
              title: 'general.notifications.update',
              content: 'proposal.create.notifications.update_success'
            })
          });
    },
  },
  computed: {
    ...mapGetters(['isProposalEditable', 'proposalValidTo', 'proposalValidFrom']),
    validFromFormatted(): string | null {
      if (!this.proposalValidFrom) {
        return null;
      }

      return new Date(this.proposalValidFrom).toLocaleDateString();
    },
    validToFormatted(): string | null {
      if (!this.proposalValidTo) {
        return null;
      }

      return new Date(this.proposalValidTo).toLocaleDateString();
    },
    areDatesValid(): boolean {
      return !!this.settings.validTo && !!this.settings.validFrom && (new Date(this.settings.validFrom) <= new Date(this.settings.validTo));
    },
    isDescriptionEmpty(): boolean {
      return !this.settings.description?.trim();
    },
    areInitialsEmpty(): boolean {
      // Initials are only enforced while the proposal is editable; non-editable proposals can
      // still update their description through this modal even when initials were never set.
      if (!this.isProposalEditable) {
        return false;
      }

      return !this.settings.createdBy?.trim() || !this.settings.requestedBy?.trim();
    }
  }
})
